import * as Phaser from 'phaser';
import { BALANCE } from '@/config/balance';
import { GAME_WIDTH } from '@/config/gameConfig';

export type LaneDirection = -1 | 1;
export type InputSource = 'keyboard' | 'swipe' | 'tap';
export type LaneInputListener = (dir: LaneDirection, source: InputSource, key?: string) => void;

const TAP_MAX_MOVEMENT_PX = 10;

export class InputSystem {
  private scene: Phaser.Scene;
  private listener: LaneInputListener;
  private enabled = false;
  private downX = 0;
  private downY = 0;
  private downTime = 0;
  private downActive = false;

  constructor(scene: Phaser.Scene, listener: LaneInputListener) {
    this.scene = scene;
    this.listener = listener;
    this.bindPointer();
    this.bindKeyboard();
    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
  }

  setEnabled(value: boolean): void {
    this.enabled = value;
  }

  destroy(): void {
    this.scene.input.off('pointerdown', this.onPointerDown, this);
    this.scene.input.off('pointerup', this.onPointerUp, this);
    window.removeEventListener('keydown', this.onKeyDown);
  }

  private bindPointer(): void {
    this.scene.input.on('pointerdown', this.onPointerDown, this);
    this.scene.input.on('pointerup', this.onPointerUp, this);
  }

  private bindKeyboard(): void {
    // Dùng window keydown + e.code (physical position) thay vì Phaser keyCode (layout-dependent).
    // Đảm bảo D physical key luôn 'KeyD' bất kể layout (US/AZERTY/Dvorak/VN telex).
    window.addEventListener('keydown', this.onKeyDown);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.repeat) return;
    // Bỏ qua IME composition (Vietnamese Telex/VNI: dd→đ, aa→â làm key event sai)
    if (e.isComposing || e.keyCode === 229) return;
    let dir: LaneDirection | undefined;
    let name = '';
    switch (e.code) {
      case 'KeyA':
      case 'ArrowLeft':
        dir = -1;
        name = e.code;
        break;
      case 'KeyD':
      case 'ArrowRight':
        dir = 1;
        name = e.code;
        break;
      default:
        return;
    }
    e.preventDefault();
    if (!this.enabled) return;
    this.listener(dir, 'keyboard', name);
  };

  private onPointerDown(p: Phaser.Input.Pointer): void {
    this.downX = p.x;
    this.downY = p.y;
    this.downTime = this.scene.time.now;
    this.downActive = true;
  }

  private onPointerUp(p: Phaser.Input.Pointer): void {
    if (!this.downActive) return;
    this.downActive = false;
    if (!this.enabled) return;

    const dx = p.x - this.downX;
    const dy = p.y - this.downY;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    const dt = this.scene.time.now - this.downTime;

    const isSwipe =
      absX >= BALANCE.lanes.swipeThresholdPx &&
      absX > absY &&
      dt <= BALANCE.lanes.swipeMaxDurationMs;
    if (isSwipe) {
      this.listener(dx > 0 ? 1 : -1, 'swipe');
      return;
    }

    // Tap-half chỉ cho touch (mobile). Mouse click trên desktop không trigger lane.
    const isTouch = isTouchPointer(p);
    if (!isTouch) return;
    const isTap = absX < TAP_MAX_MOVEMENT_PX && absY < TAP_MAX_MOVEMENT_PX;
    if (isTap) {
      this.listener(p.x < GAME_WIDTH / 2 ? -1 : 1, 'tap');
    }
  }
}

function isTouchPointer(p: Phaser.Input.Pointer): boolean {
  const evt = (p as unknown as { event?: PointerEvent }).event;
  if (evt && typeof evt.pointerType === 'string') {
    return evt.pointerType === 'touch' || evt.pointerType === 'pen';
  }
  return p.wasTouch === true;
}
